import { isSameMinute, subMonths } from 'date-fns';
import { MeasurementData } from 'features/weather/types';
import {
  convertFromImperial,
  convertNumberToSaveToDB,
  getLabelForMeasurementType,
} from 'features/weather/utils/dataConverters';
import { TraceHistory, Weather } from 'types';
import { HourlyData } from 'types/models/ForecastDto';
import { WeatherConstraint } from '../types';

export function changeArrayAtIndex<T>(array: T[], value: T, idx: number) {
  return array
    .slice(0, idx)
    .concat(value)
    .concat(array.slice(idx + 1));
}

export function removeElementAtIndex<T>(array: T[], idx: number) {
  return array.slice(0, idx).concat(array.slice(idx + 1));
}

export const filterTraces = (
  traceHistory: TraceHistory[],
  weatherData: Weather[],
  weatherConstraints: WeatherConstraint[],
  inImperial: boolean | null | undefined
) => {
  const newFilteredTraceHistory = traceHistory.filter((historyDatum) => {
    const matchingWeather = weatherData.find((weatherDatum) =>
      isSameMinute(new Date(weatherDatum.time), new Date(historyDatum.time))
    );
    if (!matchingWeather) return false;
    for (const constraint of weatherConstraints) {
      if (!constraint.measurement) continue;
      const weatherLevel =
        matchingWeather[constraint.measurement as MeasurementData];
      if (weatherLevel === null || weatherLevel === undefined) return false;
      const constraintLevel = convertFromImperial(
        convertNumberToSaveToDB(
          Number(constraint.level),
          constraint.measurement as MeasurementData
        ),
        constraint.measurement as MeasurementData,
        inImperial
      );
      if (constraintLevel === undefined || constraintLevel === null)
        return true;
      if (constraint.greaterThan) {
        if (weatherLevel < constraintLevel) return false;
      } else {
        if (weatherLevel > constraintLevel) return false;
      }
    }
    return true;
  });
  return newFilteredTraceHistory;
};

export const filterForecasts = (
  forecasts: HourlyData[],
  weatherConstraints: WeatherConstraint[],
  inImperial: boolean | null | undefined
) => {
  if (weatherConstraints.length === 0) return [];
  const newFilteredForecasts = forecasts.filter((forecast) => {
    for (const constraint of weatherConstraints) {
      if (!constraint.measurement) continue;
      const forecastLevel = forecast[constraint.measurement as MeasurementData];
      if (forecastLevel === null || forecastLevel === undefined) return false;
      const constraintLevel = convertFromImperial(
        convertNumberToSaveToDB(
          Number(constraint.level),
          constraint.measurement as MeasurementData
        ),
        constraint.measurement as MeasurementData,
        inImperial
      );
      if (constraintLevel === null || constraintLevel === undefined)
        return false;
      if (constraint.greaterThan) {
        if (forecastLevel <= constraintLevel) return false;
      } else {
        if (forecastLevel >= constraintLevel) return false;
      }
    }
    return true;
  });
  return newFilteredForecasts;
};

export const getStartDateFromDurationEnum = (durationNum: number | string) => {
  if (Number(durationNum) === 1) return subMonths(new Date(), 3);
  if (Number(durationNum) === 2) return subMonths(new Date(), 6);
  if (Number(durationNum) === 3) return subMonths(new Date(), 12);
  return new Date();
};

export const getEnumDurationFromMonthDifference = (
  difference: number | string
) => {
  if (Number(difference) === 3) return 1;
  if (Number(difference) === 6) return 2;
  if (Number(difference) === 12) return 3;
  return 2;
};

export const getStringRepresentationOfConstraints = (
  constraints: WeatherConstraint[],
  inImperial: boolean | null | undefined
) => {
  let result = '';
  for (const constraint of constraints) {
    if (
      !constraint.measurement ||
      constraint.level === null ||
      constraint.level === undefined ||
      constraint.level === '' ||
      isNaN(Number(constraint.level))
    )
      continue;
    result +=
      getLabelForMeasurementType(constraint.measurement, inImperial) +
      (constraint.greaterThan ? ' > ' : ' < ') +
      constraint.level +
      ', ';
  }
  if (result === '') return 'None';
  return result.slice(0, -2);
};
